import { useEffect, useState } from 'react';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useCatalogMetricsContext } from 'src/contexts/CatalogMetricsContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useListingStatusContext } from 'src/contexts/ListingStatusContext';
import { Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ListingWithEvent } from 'src/tables/ListingTable/ListingTable.types';
import { PosManagedMarketplace } from 'src/utils/constants/constants';
import { compareMarketplacePutMainMarketplacesFirst } from 'src/utils/eventWithDataUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  Feature,
  ListingClient,
  ListingDetails,
  ListingMetrics,
  ListingStatusInfo,
  Marketplace,
} from 'src/WebApiController';

import {
  useClientSideMarketplaceListingStatus,
  useMarketplaceBroadcastSection,
} from '../MarketplaceBroadcastSection.hooks';
import {
  hasPendingMarketplaces,
  updateMarketplaceStatusesAndListing,
} from '../MarketplaceBroadcastSection.utils';
import { MarketplaceBroadcastIcon } from './MarketplaceBroadcastIcon';

export const MarketplaceBroadcastCell = ({
  listingWithEvent,
  disabled,
}: {
  listingWithEvent: ListingWithEvent;
  disabled?: boolean;
}) => {
  const listing = listingWithEvent.listing!;

  const hasReducedBroadcastStatusPullingFrequencyFeature = useUserHasFeature(
    Feature.ReducedBroadcastStatusPullingFrequency
  );

  const [marketplaceListingStatus, setMarketplaceListingStatus] =
    useClientSideMarketplaceListingStatus(listing);

  const { posEntity, setActivePosEntity, updateActivePosEntityInfo } =
    useActivePosEntityContext<ListingDetails>();

  const { updateItemInEvent } = useCatalogDataContext();

  const { refreshMetrics } = useCatalogMetricsContext<ListingMetrics>();

  const [isLoading, setIsLoading] = useState(false);

  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();

  const { mkpListings } = listing;

  const { getMarketplaceDisplayProps, event, isInternationalEvent } =
    useMarketplaceBroadcastSection(
      listing,
      disabled ?? false,
      setIsLoading,
      marketplaceListingStatus,
      setMarketplaceListingStatus,
      listingWithEvent.event,
      listingWithEvent.performer,
      listingWithEvent.venue
    );

  const hasBatchGetListingStatusCallsFeature = useUserHasFeature(
    Feature.BatchGetListingStatusCalls
  );

  const { getOrSubscribeForListingStatus, isLoading: statusIsLoading } =
    useListingStatusContext();

  useEffect(() => {
    const pollingFrequency = hasBatchGetListingStatusCallsFeature
      ? 2_000
      : hasReducedBroadcastStatusPullingFrequencyFeature
        ? 6_000
        : 2_000;

    // If we have any Marketplace listing pending status - we need to poll to get the latest status
    const statusChecking = setInterval(async () => {
      const shouldCheckStatus =
        hasPendingMarketplaces(marketplaceListingStatus) &&
        !isLoading &&
        !statusIsLoading;

      if (shouldCheckStatus) {
        await tryInvokeApi(
          async () => {
            setIsLoading(true);

            let newListing: ListingStatusInfo | undefined = undefined;
            if (hasBatchGetListingStatusCallsFeature) {
              // When we have batch call feature, we should just skip the per-listing polling
              newListing = getOrSubscribeForListingStatus(listing.id);
            } else {
              const client = new ListingClient(activeAccountWebClientConfig);
              newListing = await client.getListingStatusByListingId(listing.id);
            }

            if (newListing) {
              updateMarketplaceStatusesAndListing(
                statusChecking,
                listing,
                newListing,
                marketplaceListingStatus,
                setMarketplaceListingStatus,
                (updatedListing) => {
                  refreshMetrics?.();
                  updateItemInEvent(
                    updatedListing,
                    ActionOutboxEntityType.Listing
                  );
                  if (posEntity && updatedListing.id === posEntity.id) {
                    updateActivePosEntityInfo({
                      posEntity: updatedListing,
                      posEntityId: updatedListing!.id,
                      posEntityDisplayId: updatedListing!.idOnMkp,
                    });
                  }
                }
              );
            }
          },
          (error) => {
            // Because this is a background process - we don't want to show error dialog
            trackError('ListingClient.getListingStatusByListingId', error, {
              listingId: listing.id,
            });
          },
          () => setIsLoading(false)
        );
      }
    }, pollingFrequency);

    return () => clearInterval(statusChecking);
  }, [
    activeAccountWebClientConfig,
    event,
    listing,
    listing.id,
    listing.idOnMkp,
    marketplaceListingStatus,
    mkpListings,
    refreshMetrics,
    setActivePosEntity,
    updateActivePosEntityInfo,
    trackError,
    isLoading,
    setMarketplaceListingStatus,
    updateItemInEvent,
    posEntity,
    hasReducedBroadcastStatusPullingFrequencyFeature,
    hasBatchGetListingStatusCallsFeature,
    getOrSubscribeForListingStatus,
    statusIsLoading,
  ]);

  return (
    <Stack direction="row" gap="m">
      {mkpListings
        ?.filter((ml) => ml.mkp && !PosManagedMarketplace.includes(ml.mkp))
        .toSorted((a, b) =>
          compareMarketplacePutMainMarketplacesFirst(a.mkp, b.mkp)
        )
        .flatMap((ml, i) => {
          if (ml.mkp === Marketplace.StubHub) {
            return [
              <MarketplaceBroadcastIcon
                {...getMarketplaceDisplayProps(ml, false, i)}
                key={`${ml.mkpListingId}-${ml.mkp}-${false}`}
                event={event!}
                listing={listing}
              />,
              <MarketplaceBroadcastIcon
                {...getMarketplaceDisplayProps(ml, true, i)}
                key={`${ml.mkpListingId}-${ml.mkp}-${true}`}
                event={event!}
                listing={listing}
              />,
            ];
          }

          return !isInternationalEvent
            ? [
                // We only show other marketplaces when the Event is US
                <MarketplaceBroadcastIcon
                  {...getMarketplaceDisplayProps(ml, false, i)}
                  key={`${ml.mkpListingId}-${ml.mkp}-${false}`}
                  event={event!}
                  listing={listing}
                />,
              ]
            : [];
        })}
    </Stack>
  );
};
